// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@usealloy/component-library';

import type { PopulatedThreshold } from '~/dashboard/views/workflows/store/engine/types';
import { OPERATORS } from 'flow/editor/components/thresholds/operators';
import { RuleSummaryText } from './styles';
import { formatValue } from './RuleBreakdown';
import { TAG_OPERATORS } from '../../constants';
import { getActionName, isActionOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import { getServiceAttribute } from '../../helpers';
import { useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';


export type RuleSummaryProps = {
  threshold: PopulatedThreshold;
  thresholdValue: number;
};

const RuleSummary: React.FC<RuleSummaryProps> = ({ threshold, thresholdValue }) => {
  const workflowData = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);
  const { left, operator, right } = threshold;

  /**
   * If we pass in a `thresholdValue` that is editable, we want `ruleValue` to reflect its changes,
   * but only after initial mount. At initial mount, we want `ruleValue` to render the value set
   * from the Node Drawer.
   * */
  const isInitialMount = useRef(true);
  const [ruleValue, setRuleValue] = useState(
    (right.attributeValue !== null && right.attributeValue !== undefined)
      ? formatValue(Number(right.attributeValue))
      : 'N/A',
  );
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setRuleValue(thresholdValue ? formatValue(thresholdValue) : 'N/A');
    }
  }, [thresholdValue]);

  const isTagOperator = TAG_OPERATORS.includes(operator);
  const formattedOperator = isTagOperator ? operator : OPERATORS[operator].symbol;

  const serviceAttribute = isActionOfType(left.action, ActionType.SERVICE)
    ? getServiceAttribute(threshold, 'left', workflowData, workflowProps)
    : null;

  return (
    <Box justifyContent="flex-start">
      <RuleSummaryText>
        If{' '}
        <span style={{ color: 'var(--cl-green-400)' }}>
          {getActionName(left.action)}
        </span>
        {serviceAttribute && `: ${serviceAttribute.display_name}`}
        {` is ${formattedOperator} `}
        {!isTagOperator && (
          <span style={{ fontWeight: 600, color: 'var(--cl-gray-1100)' }}>
            {ruleValue}
          </span>
        )}
      </RuleSummaryText>
    </Box>
  );
};

export default RuleSummary;
